/**
 * Type Transformers - Pure Functions
 *
 * Pure transformation functions for converting API types to frontend domain types.
 * All functions are side-effect free and testable in isolation.
 */

import type { Pickem, PickemQuestion } from '@low6-pickem/api-types';
import type { Competition, Question, GameState } from '@/types/domain';
import { getQuestionId, getOptionId } from './id-helpers';
import { getLocalizedField } from './localized-field';
import { formatPrizeLabel, type UserPrizeData } from './prize';
import i18n from '@/i18n';

/** Pickem fields attached by the backend for the current user (extra to OpenAPI until types:update). */
type PickemWithUserFields = Pickem & {
  user_ranking?: number | null;
  total_points?: number | null;
  user_prize?: UserPrizeData | null;
};

// =============================================================================
// Main Transformation Function
// =============================================================================

/**
 * Transforms a Pickem API response to a Competition domain object
 *
 * @param pickem - The pickem object from the API
 * @param locale - Language to resolve `_en`/`_fr`/`_nl` fields in. Taken explicitly so
 *   callers memoising this transform have the language as a visible dependency;
 *   defaults to the active language for callers outside React.
 * @returns Transformed Competition object
 */
export function transformPickemToCompetition(
  pickem: Pickem,
  locale: string = i18n.language
): Competition {
  // Create a valid date from start field or start_utc_timestamp
  const dateString = pickem.start;
  const gameDate = dateString
    ? new Date(dateString)
    : pickem.start_utc_timestamp
      ? new Date(pickem.start_utc_timestamp * 1000)
      : new Date();

  const gameState = mapPickemStatusToGameState(pickem.status, gameDate);

  const userPickem = pickem as PickemWithUserFields;
  const myPrizeLabel = formatPrizeLabel(userPickem.user_prize);

  return {
    id: pickem.pickem_id,
    title: getLocalizedField(pickem, 'title', locale),
    prize:
      getLocalizedField(pickem, 'prize_text', locale) || i18n.t('game.labels.prizePoolFallback'),
    state: gameState,
    gameDate,
    finalScore:
      typeof userPickem.user_ranking === 'number' && typeof userPickem.total_points === 'number'
        ? {
            rank: userPickem.user_ranking,
            points: userPickem.total_points,
          }
        : undefined,
    currentPoints:
      typeof userPickem.total_points === 'number' && gameState === 'live'
        ? userPickem.total_points
        : undefined,
    myPrize: myPrizeLabel ?? undefined,
    totalEntries: typeof pickem.entries === 'number' ? pickem.entries : undefined,
    correctPicks: pickem.correct_picks,
    gamesCount: typeof pickem.games_count === 'number' ? pickem.games_count : undefined,
    questions: transformQuestions(pickem.questions, locale),
    tieBreaker: extractTieBreaker(pickem.questions, locale),
    hasSelections: pickem.has_entered,
    isOpen: pickem.open ?? true,
    sport: pickem.sport as Competition['sport'],
  };
}

// =============================================================================
// Game State Mapping
// =============================================================================

/**
 * Maps Pickem API status to frontend GameState
 *
 * @param status - Status from API (can be string or number)
 * @param startDate - The start date of the game
 * @returns The appropriate GameState
 */
export function mapPickemStatusToGameState(status: unknown, startDate: Date): GameState {
  // Normalize status to uppercase string
  const statusStr = String(status).toUpperCase();

  // If completed, always return final
  if (
    statusStr === 'COMPLETE' ||
    statusStr === 'COMPLETED' ||
    statusStr === 'FINAL' ||
    status === 4
  ) {
    return 'final';
  }

  // INPLAY / LIVE: contest is live and scoring
  if (statusStr === 'INPLAY' || statusStr === 'LIVE') {
    return 'live';
  }

  const now = new Date();
  const hasStarted = startDate.getTime() <= now.getTime();

  // OPEN / PENDING / ACTIVE: accepting entries until start, then live
  // (picks frozen; question chrome uses locked styles until graded).
  if (
    statusStr === 'OPEN' ||
    statusStr === 'PENDING' ||
    statusStr === 'ACTIVE' ||
    status === 1 ||
    status === 2 ||
    status === 3
  ) {
    return hasStarted ? 'live' : 'countdown';
  }

  // DRAFT / SCHEDULED: not yet open — show countdown
  if (statusStr === 'DRAFT' || statusStr === 'SCHEDULED') {
    return 'countdown';
  }

  // Default: started → live, otherwise countdown
  return hasStarted ? 'live' : 'countdown';
}

// =============================================================================
// Question Transformations
// =============================================================================

/**
 * Transforms array of PickemQuestions to frontend Question objects
 * Filters out tiebreaker questions
 *
 * @param questions - Array of questions from API
 * @param locale - Language to resolve `_en`/`_fr`/`_nl` fields in; defaults to the
 *   active language for callers outside React.
 * @returns Array of transformed Question objects
 */
export function transformQuestions(
  questions: PickemQuestion[],
  locale: string = i18n.language
): Question[] {
  if (!questions || !Array.isArray(questions)) return [];

  return questions
    .filter(q => {
      // Filter out tiebreaker questions using multiple detection methods
      // Primary: question_type === 'tiebreaker'
      // Fallback: is_tiebreak flag
      return q.question_type !== 'tiebreaker' && !q.is_tiebreak;
    })
    .map(q => ({
      id: getQuestionId(q),
      type: q.category === 'prop' ? 'prop' : 'team',
      teams: extractTeamIds(q),
      question: q.category === 'prop' ? getLocalizedField(q, 'title', locale) : undefined,
      points: `${q.points}pts`,
      percentages: calculatePercentages(q),
      fixtureId: q.sport_match_data?.id ?? undefined,
    }));
}

/**
 * Extracts team IDs from question options
 * Returns first two option IDs as a tuple
 *
 * @param question - The question object
 * @returns Tuple of two team IDs
 */
export function extractTeamIds(question: PickemQuestion): [string, string] {
  const teamIds = question.options.slice(0, 2).map(opt => getOptionId(opt));

  return [teamIds[0] || 'team1', teamIds[1] || 'team2'];
}

/**
 * Calculates yes/no percentages for prop questions
 * Returns undefined for non-prop questions
 *
 * @param question - The question object
 * @returns Percentages object or undefined
 */
export function calculatePercentages(
  question: PickemQuestion
): { yes: number; no: number } | undefined {
  if (question.category !== 'prop') return undefined;

  const yesOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('yes'));
  const noOption = question.options.find(opt => opt.title_en?.toLowerCase().includes('no'));

  return {
    yes: yesOption?.popularity_pct || 50,
    no: noOption?.popularity_pct || 50,
  };
}

// =============================================================================
// Tiebreaker Extraction
// =============================================================================

/**
 * Extracts tiebreaker information from questions array
 * Returns default tiebreaker if none found
 *
 * @param questions - Array of questions from API
 * @param locale - Language to resolve `_en`/`_fr`/`_nl` fields in; defaults to the
 *   active language for callers outside React.
 * @returns Tiebreaker configuration object
 */
export function extractTieBreaker(
  questions: PickemQuestion[],
  locale: string = i18n.language
): Competition['tieBreaker'] {
  if (!questions || !Array.isArray(questions)) {
    return {
      description: i18n.t('tiebreaker.defaultDescription'),
      teams: ['team1', 'team2'],
    };
  }

  const tieBreaker = questions.find(q => q.question_type === 'tiebreaker' || q.is_tiebreak);

  if (tieBreaker) {
    return {
      description:
        getLocalizedField(tieBreaker, 'description', locale) ||
        getLocalizedField(tieBreaker, 'title', locale),
      teams: extractTeamIds(tieBreaker),
    };
  }

  // Default tiebreaker if none found
  return {
    description: i18n.t('tiebreaker.defaultDescription'),
    teams: ['team1', 'team2'],
  };
}
